/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import {
  ChatError,
  MaybeOptimisticChatTextMessage,
} from "@lazarentertainment/public-chat-web"
import { ChatBanFragment } from "@lazarentertainment/public-chat-web/dist/src/plugin-service/build"
import {
  PublicChatProvider,
  useChatCursor,
  usePublicChatContext,
} from "@lazarentertainment/public-chat-web/dist/src/react"
import { randomString } from "@lazarentertainment/public-chat-web/dist/src/util/randomString"
import * as AllIcons from "@lazarentertainment/web-design-system/components/iconSolid"
import { captureException } from "@sentry/nextjs"
import { useAuthContext } from "client/components/auth"
import { getAccessToken } from "client/components/auth/helpers"
import { useChannelContext } from "client/components/channel"
import { useConfigContext } from "client/components/configuration/ConfigContext"
import { useVideoContext } from "client/packages/player/player/VideoContext"
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react"
import { usePreviousDifferent } from "rooks"

import { ChatView } from "../constants"

export type ChatUiState = {
  messages: Array<ChatNotification | MaybeOptimisticChatTextMessage>
  writeReady: boolean
  isReplay: boolean
  chatError: ChatError | null
  clearChatError: () => void
  isChatOpen: boolean
  setIsChatOpen: (isOpen: boolean) => void
  messageInputValue: string
  setMessageInputValue: (value: string) => void
  chatView: ChatView
  setChatView: (view: ChatView) => void
  isMobileView: boolean
  setIsMobileView: (isMobile: boolean) => void
  isJoinModalOpen: boolean
  setIsJoinModalOpen: (isOpen: boolean) => void
  isBannedUsersModalOpen: boolean
  setIsBannedUsersModalOpen: (isOpen: boolean) => void
  isDisableChatModalOpen: boolean
  setIsDisableChatModalOpen: (isOpen: boolean) => void
  isUnbanModalOpen: boolean
  setIsUnbanModalOpen: (isOpen: boolean) => void
  unbanModalUser: { userId: string; sourceName: string } | null
  setUnbanModalUser: (
    user: { userId: string; sourceName: string } | null,
  ) => void
  addLocalNotification: (notification: ChatNotificationInput) => void
}

export const defaultChatUiState = {
  messages: [],

  writeReady: false,

  isReplay: false,

  chatError: null,
  clearChatError: () => {},

  // Whether chat has been opened by the user, client-side. Applies to mobile only
  isChatOpen: false,
  setIsChatOpen: () => {},

  messageInputValue: "",
  setMessageInputValue: () => {},

  chatView: ChatView.DEFAULT,
  setChatView: () => {},

  isMobileView: false,
  setIsMobileView: () => {},

  isJoinModalOpen: false,
  setIsJoinModalOpen: () => {},

  isBannedUsersModalOpen: false,
  setIsBannedUsersModalOpen: () => {},

  isDisableChatModalOpen: false,
  setIsDisableChatModalOpen: () => {},

  isUnbanModalOpen: false,
  setIsUnbanModalOpen: () => {},

  unbanModalUser: null,
  setUnbanModalUser: () => {},

  addLocalNotification: () => {},
}

export const ChatContext = createContext<ChatUiState>(defaultChatUiState)

type InternalChatProviderProps = {
  chatIsInitialized: boolean
  children: ReactNode
}

function InternalChatProvider({
  children,
  chatIsInitialized,
}: InternalChatProviderProps) {
  const [isChatOpen, setIsChatOpen] = useState(
    defaultChatUiState.isChatOpen,
  )
  const [chatView, setChatView] = useState(defaultChatUiState.chatView)
  const [isMobileView, setIsMobileView] = useState(
    defaultChatUiState.isMobileView,
  )
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(
    defaultChatUiState.isJoinModalOpen,
  )
  const [isBannedUsersModalOpen, setIsBannedUsersModalOpen] =
    useState<boolean>(defaultChatUiState.isBannedUsersModalOpen)
  const [isDisableChatModalOpen, setIsDisableChatModalOpen] =
    useState<boolean>(defaultChatUiState.isDisableChatModalOpen)
  const [isUnbanModalOpen, setIsUnbanModalOpen] = useState(
    defaultChatUiState.isUnbanModalOpen,
  )
  const [unbanModalUser, setUnbanModalUser] = useState<{
    userId: string
    sourceName: string
  } | null>(null)
  const [messageInputValue, setMessageInputValue] = useState("")
  const [chatError, setChatError] = useState<ChatError>(null)
  const [writeReady, setWriteReady] = useState(false)

  const [notifications, addLocalNotification] = useReducer(
    notificationsReducer,
    [],
  )

  const [messagesAndNotifications, mergeMessagesAndNotifications] =
    useReducer(messagesAndNotificationsReducer, [])
  /* Memoize this list, because every time the time cursor moves (~4 times per scecond),
   * we regenerate the list. So it's the often the exact same as before.
   * Prevents unnecessary re-renders of the chat UI, and unnecessary event emissions.
   */
  const memoizedMessagesAndNotifications = useMemo(() => {
    return messagesAndNotifications
  }, [
    messagesAndNotifications.length,
    messagesAndNotifications[0]?.timestampMs,
    messagesAndNotifications[messagesAndNotifications.length - 1]
      ?.timestampMs,
  ])

  const addBanNotification = ({ displayName }: ChatBanFragment) => {
    addLocalNotification({
      text: `You've banned ${displayName} from the chat`,
    })
  }

  const addUnbanNotification = ({ displayName }: ChatBanFragment) => {
    addLocalNotification({
      text: `You've unbanned ${displayName} from the chat`,
    })
  }

  const { recording } = useChannelContext()

  const {
    init: reinitChat,
    messages: chatTextMessages,
    pluginState,
    displayName,
    clearState,
    loadMessages,
    loadBans,
    onChatEvent,
  } = usePublicChatContext()

  const previousPluginState = usePreviousDifferent(pluginState)

  // If we have write access and a displayName then ensure the join modal is no longer
  // open. This applies in the case of initially setting a display name OR when the
  // user changes their name
  useEffect(() => {
    if (writeReady && displayName) {
      setIsJoinModalOpen(false)
    }
  }, [writeReady, displayName])

  useEffect(() => {
    if (
      displayName &&
      chatIsInitialized &&
      !writeReady &&
      pluginState === "enabled"
    ) {
      setWriteReady(true)
      if (!recording) {
        addLocalNotification({
          text: `You've joined the chat as ${displayName}`,
          icon: "UserSolid",
        })
      }
    }
  }, [displayName, chatIsInitialized, pluginState])

  useEffect(() => {
    const unsubscribes = [onChatEvent("error", setChatError)]
    if (!recording) {
      unsubscribes.push(
        onChatEvent("ban", addBanNotification),
        onChatEvent("unban", addUnbanNotification),
      )
    }
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe())
  }, [])

  useEffect(() => {
    if (chatError?.code === "DisplayNameRejectedError") {
      setIsJoinModalOpen(true)
    }
  }, [chatError])

  useEffect(() => {
    // do not show chat joined notification for recordings as user cannot chat.
    if (displayName && writeReady && !recording) {
      addLocalNotification({
        text: `Name changed to ${displayName}`,
        icon: "EditSolid",
      })
    }
  }, [displayName])

  const {
    auth: { user },
  } = useAuthContext()

  const previousUserId = usePreviousDifferent(user?.userId)

  useEffect(() => {
    if (user?.userId !== previousUserId) {
      // Changes to user requires a new grant
      reinitChat().catch(captureException)
    }

    const accessToken = getAccessToken()
    if (!user && writeReady && !accessToken && !recording) {
      // If user already has write access, but no access token upon
      // pubnub initialization, we can assume the user has logged out
      addLocalNotification({
        text: "You've signed out",
      })
    }
  }, [user])

  useEffect(() => {
    if (user?.userId) {
      if (!recording) {
        addLocalNotification({
          text: "You've signed in",
        })
      }

      loadBans()
    }
  }, [user?.userId])

  useEffect(() => {
    if (recording) {
      return
    }
    clearState()
    addLocalNotification(null)
    if (previousPluginState === "disabled" && pluginState === "enabled") {
      addLocalNotification({
        text: "Chat has been enabled",
      })
    } else if (pluginState === "disabled") {
      setMessageInputValue("")
      setIsJoinModalOpen(false)
      addLocalNotification({
        text: "Chat has been disabled",
      })
    }
  }, [pluginState])

  const { videoTimestamp, isSeeking } = useVideoContext()

  const recordingStartTime = useMemo<number | null>(() => {
    if (!recording) {
      return null
    }

    // If we should be displaying the untrimmed VOD, or the trimStartTime is 0
    // (special case to indicate that the recording is not trimmed), we can use
    // the startTime directly.
    if (recording.displayUntrimmedVod || recording.trimStartTime === 0) {
      return Date.parse(recording.startTime)
    }

    return Date.parse(recording.startTime) + recording.trimStartTime
  }, [
    recording?.displayUntrimmedVod,
    recording?.startTime,
    recording?.trimStartTime,
  ])

  const recordingEndTime = useMemo<number | null>(() => {
    if (!recording) {
      return null
    }

    // If we should be displaying the untrimmed VOD, or the trimEndTime is 0
    // (special case to indicate that the recording is not trimmed), we can use
    // the endTime directly.
    if (recording.displayUntrimmedVod || recording.trimEndTime === 0) {
      return Date.parse(recording.endTime)
    }

    // Since trimEndTime is measured from the start of the recording, we can
    // add it to the parsed startTime to determine the end time.
    return Date.parse(recording.startTime) + recording.trimEndTime
  }, [
    recording?.displayUntrimmedVod,
    recording?.startTime,
    recording?.endTime,
    recording?.trimEndTime,
  ])

  const { cursor, setCursor } = useChatCursor(
    recordingStartTime && recordingEndTime
      ? {
          minTimestamp: recordingStartTime,
          maxTimestamp: recordingEndTime,
          // TODO: Load reactions for VOD
          loadActionsStrategy: "none",
        }
      : {},
  )

  useEffect(() => {
    mergeMessagesAndNotifications({
      messages: [...chatTextMessages.values()].filter(
        ({ timetoken }) =>
          timetoken === undefined ||
          cursor === undefined ||
          timetoken <= cursor,
      ),
      notifications,
    })
  }, [cursor, chatTextMessages, notifications])

  useEffect(() => {
    if (!chatIsInitialized || recording || pluginState !== "enabled") {
      return
    }
    // Only load the latest messages if we're in live mode
    loadMessages({
      type: "latest",
      seconds: 60 * 20,
      loadActionsStrategy: "include",
    })
  }, [chatIsInitialized, pluginState])

  useEffect(() => {
    if (!chatIsInitialized) {
      return
    }

    // Only set the cursor for VOD
    if (typeof recordingStartTime === "number" && !isSeeking) {
      setCursor(recordingStartTime + Math.round(videoTimestamp * 1000))
    }
  }, [chatIsInitialized, recordingStartTime, isSeeking, videoTimestamp])

  const state = {
    messages: memoizedMessagesAndNotifications,

    writeReady,

    isReplay: !!recording,

    chatError,
    clearChatError: () => setChatError(null),

    // Whether chat has been opened by the user, client-side. Applies to mobile only
    isChatOpen,
    setIsChatOpen,

    messageInputValue,
    setMessageInputValue,

    chatView,
    setChatView,

    isMobileView,
    setIsMobileView,

    isJoinModalOpen,
    setIsJoinModalOpen,

    isBannedUsersModalOpen,
    setIsBannedUsersModalOpen,

    isDisableChatModalOpen,
    setIsDisableChatModalOpen,

    isUnbanModalOpen,
    setIsUnbanModalOpen,

    unbanModalUser,
    setUnbanModalUser,

    addLocalNotification,
  }

  return (
    <ChatContext.Provider value={state}>{children}</ChatContext.Provider>
  )
}

export type ChatProviderProps = {
  children?: ReactNode
}

export function ChatProvider({ children }: ChatProviderProps) {
  const { recordingId, channelId } = useChannelContext()
  const config = useConfigContext()
  const [chatIsInitialized, setChatIsInitialized] = useState(false)

  return (
    <PublicChatProvider
      s10ChannelId={channelId}
      pubnubPublishKey={config.pubnub.publishKey}
      pubnubSubscribeKey={config.pubnub.subscribeKey}
      environment={config.api.platformName}
      mode={recordingId ? "replay" : "live"}
      onInit={() => {
        setChatIsInitialized(true)
      }}
    >
      <InternalChatProvider chatIsInitialized={chatIsInitialized}>
        {children}
      </InternalChatProvider>
    </PublicChatProvider>
  )
}

export const useChatContext = () => {
  const chatContext = useContext(ChatContext)
  if (!chatContext) {
    throw new Error(
      "No ChatContext.Provider found when calling useChatContext.",
    )
  }
  return chatContext
}

export type ChatNotification = {
  key: string
  text: string
  timestampMs: number
  icon?: keyof typeof AllIcons
  highlight?: boolean
}

export type ChatNotificationInput = Omit<
  ChatNotification,
  "key" | "timestampMs"
>

export function isChatNotification(
  notificationOrMessage: ChatNotification | MaybeOptimisticChatTextMessage,
): notificationOrMessage is ChatNotification {
  return !("messageKey" in notificationOrMessage)
}

/**
 * Chat messages and notifications are displayed inline together in the chat
 * history, so we merge them together according to epoch timestamp.
 */
function messagesAndNotificationsReducer(
  _: Array<ChatNotification | MaybeOptimisticChatTextMessage>,
  {
    messages,
    notifications,
  }: {
    messages: MaybeOptimisticChatTextMessage[]
    notifications: ChatNotification[]
  },
) {
  if (!messages.length) {
    return notifications
  }

  if (!notifications.length) {
    return messages
  }

  const merged: Array<ChatNotification | MaybeOptimisticChatTextMessage> =
    []
  let messageIndex = 0
  let notificationIndex = 0
  let message: MaybeOptimisticChatTextMessage
  let notification: ChatNotification

  do {
    message = messages[messageIndex]
    notification = notifications[notificationIndex]
    if (message && notification) {
      if (message.timestampMs < notification.timestampMs) {
        merged.push(message)
        messageIndex += 1
      } else {
        merged.push(notification)
        notificationIndex += 1
      }
    } else if (message) {
      merged.push(message)
      messageIndex += 1
    } else {
      merged.push(notification)
      notificationIndex += 1
    }
  } while (
    messageIndex < messages.length ||
    notificationIndex < notifications.length
  )

  return merged
}

function notificationsReducer(
  currentNotifications: ChatNotification[],
  action: ChatNotificationInput | null,
) {
  if (action) {
    return [
      ...currentNotifications,
      {
        key: randomString(),
        text: action.text,
        timestampMs: Date.now(),
        highlight: action.highlight,
        icon: action.icon,
      },
    ]
  }

  // Clear state
  return []
}
